import { Component, OnInit, Input, Output, EventEmitter, HostListener, ChangeDetectionStrategy } from "@angular/core";

import { RegisterProductBaseComponent } from "@mypxplat/xplat/features";
import { StorageService, UserService, ProductService, WindowService, environment, StorageKeys, HelperService, ContentService } from "@mypxplat/xplat/core";
import { NgbActiveModal, NgbDateStruct, NgbDate } from "@ng-bootstrap/ng-bootstrap";
import { TranslateService } from "@ngx-translate/core";
// import * as moment from 'moment';
import { format } from "date-fns";
import { Router } from "@angular/router";
import { Product } from "@mypxplat/xplat/core";
import { AppService, AvoService, FirebaseService, UsbDeviceService } from "@mypxplat/xplat/web/core";
import { RedeemHardwareOfferComponent } from "../redeem-hardware-offer/redeem-hardware-offer.component";
import { DatePickerComponent, GiveawayGiftsSplashComponent, MissingFenderSerialComponent, RegisterSuccessSplashComponent } from "..";
import { AngularFirestore } from "@angular/fire/compat/firestore";
import { filter, take } from "rxjs/operators";
import { MaskitoMask } from "@maskito/core";

/** Show giveaway splash only when PAEAPI marks this serial/key as never registered before. */
export function shouldShowGiveawayGiftsSplash(registerResponse?: { isNewRegistration?: boolean } | null): boolean {
  return registerResponse?.isNewRegistration === true;
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "myp-register-product",
  templateUrl: "register-product.component.html",
  standalone: false,
})
export class RegisterProductComponent extends RegisterProductBaseComponent implements OnInit {
  @HostListener("window:beforeunload", ["$event"])
  doSomething($event) {
    if (this.registering) {
      $event.returnValue = "Please wait until your product has been successfully registered before closing this window.";
    }
  }

  @Input() optionCardOptions: any;
  @Input() autoNavigate: boolean = true;
  @Input() mustang: boolean = false;
  @Input() registerCustomUI: any;
  // @Input() asset_id: number;
  @Output() productRegistered: EventEmitter<any> = new EventEmitter();
  private shortMask: any = [/\w/, /\w/, /\w/, /\w/, "-", /\w/, /\w/, /\w/, /\w/, "-", /\w/, /\w/, /\w/, /\w/, "-", /\w/, /\w/, /\w/, /\w/, "-", /\w/, /\w/, /\w/, /\w/];
  private longMask: any = [
    /\w/,
    /\w/,
    /\w/,
    /\w/,
    "-",
    /\w/,
    /\w/,
    /\w/,
    /\w/,
    "-",
    /\w/,
    /\w/,
    /\w/,
    /\w/,
    "-",
    /\w/,
    /\w/,
    /\w/,
    /\w/,
    "-",
    /\w/,
    /\w/,
    /\w/,
    /\w/,
    "-",
    /\w/,
    /\w/,
    /\w/,
    /\w/,
    "-",
    /\w/,
    /\w/,
    /\w/,
    /\w/,
  ];
  public mask: MaskitoMask = this.longMask;
  // Never set anywhere — always undefined at runtime (read by the template only).
  public saving: boolean;
  public upgrading: boolean;
  public maxDate: NgbDateStruct;
  public date: Date;
  public registering: boolean = false;
  public registerResponse: any;
  public dismissMessage: string;
  public registeredProduct: any;
  public licenseResult: any;
  public loadingProgressMessage: string = "Registering your product...";
  public errorMsg: string;
  public rewards: Array<any>;
  private _audioBoxSerialNumberFail: string;
  public hasUsbAccess: boolean = false;
  constructor(
    userService: UserService,
    public productService: ProductService,
    public storageService: StorageService,
    public helperService: HelperService,
    private _router: Router,
    public appService: AppService,
    public activeModal: NgbActiveModal,
    private _translate: TranslateService,
    private _win: WindowService,
    public db: AngularFirestore,
    public fbService: FirebaseService,
    public usbDeviceService: UsbDeviceService,
    public contentService: ContentService,
    private avoService: AvoService
  ) {
    super(userService);
  }

  /**
   * Determines the category based on selectedType.
   * Returns: "instrument", "software", or "recording hardware"
   */
  private getCategoryFromSelectedType(): string | undefined {
    if (!this.selectedType) return undefined;
    if (this.selectedType === "guitar") return "instrument";
    if (this.selectedType === "software") return "software";
    if (this.selectedType === "hardware") return "recording hardware";
    return undefined;
  }

  private fireRegistrationStarted(overrides?: { action?: string; errorName?: string; category?: string }) {
    if (this.userService.appId !== "fender") return;
    const te = this.avoService.trackEvent();
    if (!te || typeof te.registrationStarted !== "function") return;
    const page = this.avoService.getPageProperties({ pageName: "Register Product" });
    // Determine category: use override if provided, otherwise try to determine from selectedType
    const category = overrides?.category ?? this.getCategoryFromSelectedType();
    const extra = {
      userId_: this.user?.["fc-id"] ?? undefined,
      prsId: this.user?.id ?? undefined,
      fcId: this.user?.["fc-id"] ?? undefined,
      ...(category ? { category } : {}),
      ...overrides,
    };
    const payload = this.avoService.buildEventPayload<Record<string, any>>(page, extra, { omitNulls: true });
    te.registrationStarted(payload);
  }

  private fireRegistrationCompleted(payload: { category: string; productName: string; productType: string; productId?: string | null; sku?: string | null }) {
    if (this.userService.appId !== "fender") return;
    const te = this.avoService.trackEvent();
    if (!te || typeof te.registrationCompleted !== "function") return;
    const page = this.avoService.getPageProperties({ pageName: "Register Product" });
    const extra = {
      userId_: this.user?.["fc-id"] ?? undefined,
      category: payload.category,
      productName: payload.productName,
      productType: payload.productType,
      productId: payload.productId ?? undefined,
      sku: payload.sku ?? undefined,
      prsId: this.user?.id ?? undefined,
      fcId: this.user?.["fc-id"] ?? undefined,
    };
    const fullPayload = this.avoService.buildEventPayload<Record<string, any>>(page, extra, { omitNulls: true });
    te.registrationCompleted(fullPayload);
  }

  private fireRegistrationClosedCta() {
    if (this.userService.appId !== "fender") return;
    this.avoService.avoInitted$
      .pipe(
        filter((v) => !!v),
        take(1)
      )
      .subscribe(() => {
        const te = this.avoService.trackEvent();
        if (!te || typeof (te as any).ctaClicked !== "function") return;
        const page = this.avoService.getPageProperties({ pageName: "Register Product" });
        const extra: Record<string, any> = {
          ctaText: "registration closed",
          ctaLocation: "Register Product",
          userId_: this.user?.["fc-id"] ?? undefined,
          fcId: this.user?.["fc-id"] ?? undefined,
        };
        const payload = this.avoService.buildEventPayload<Record<string, any>>(page, extra, { omitNulls: true });
        (te as any).ctaClicked(payload);
      });
  }

  ngOnInit() {
    this.hasUsbAccess = this.usbDeviceService.checkAvailability();
    const year = parseInt(format(new Date(), "yyyy"));
    const month = parseInt(format(new Date(), "M"));
    const day = parseInt(format(new Date(), "d"));
    if (this.mustang || this.registerCustomUI?.hardwareOnly) this.selectedType = "hardware";
    this.maxDate = new NgbDate(year, month, day);
    if (this.optionCardOptions) {
      this.selectedType = "hardware";
      this.selectedOptionCardAssetId = this.optionCardOptions.asset_ids[0].asset_id;
    }
    // Fire Registration Started after selectedType is determined
    this.fireRegistrationStarted();
    if (this.mustang) this.loadingProgressMessage = "Redeeming Studio One Pro+...";
    this._translate.get("products.register.register_ab_fail").subscribe((lang) => {
      this._audioBoxSerialNumberFail = lang;
    });
  }

  keyPressed(args) {
    // Guard against undefined/null args or args.key
    if (!args || !args.key) {
      return false;
    }

    let tester = /\w/;
    // dont do anything if its a single key and its not alphanumeric.
    if (args.key.length == 1 && !tester.test(args.key)) {
      return false;
    } else if (this.productKey) {
      let rawKey = this.productKey.replace(/_|-/g, "");
      if (args.key == "Backspace" && rawKey.length == 21) {
        this.mask = this.shortMask;
      } else if (rawKey.length > 19) {
        this.mask = this.longMask;
      } else {
        this.mask = this.shortMask;
      }
    }
  }

  resetRegisterFlow(type) {
    this.registeredProduct = undefined;
    this.registering = false;
    this.registerResponse = undefined;
    this.dismissMessage = undefined;
    this.productKey = "";
    if (type == "hardware") {
      this.selectedType = "hardware";
    } else {
      this.selectedType = "software";
    }
  }

  detectSerial() {
    this.usbDeviceService.requestUSBDevice().then((device) => {
      if (device?.serialNumber) {
        this.productKey = device.serialNumber;
      } else {
        alert("A serial number could not be detected from the selected USB device.");
      }
    });
  }

  showDatePicker() {
    let modalInstance = this.appService.showModal(DatePickerComponent, { size: "lg", keyboard: true, ariaLabelledBy: "modal-title" });
    modalInstance.componentInstance.earliestYear = new Date().getFullYear() - (this.selectedType == "guitar" ? 30 : 1);
    modalInstance.componentInstance.dateSelected.subscribe((date) => {
      this.date = date;
    });
  }

  performRegister() {
    if (this.productKey) {
      let rawKey = this.productKey.replace(/_|-/g, "");

      const lowerSerial = rawKey.toLowerCase();
      if (lowerSerial.substring(2, 0) == "u3") {
        if (lowerSerial.substring(2, 3) == "o" || lowerSerial.substring(3, 4) == "1" || lowerSerial.substring(3, 4) == "l" || lowerSerial.substring(4, 5) == "o") {
          if (!confirm(this._audioBoxSerialNumberFail)) {
            return false;
          }
        }
      }
      if (this.productKey.includes("-____-____")) {
        this.productKey = this.productKey.slice(0, -10);
      }

      if (this.selectedType == "hardware" || rawKey.length == 20 || rawKey.length == 28 || this.selectedType == "guitar") {
        if (this.selectedType == "software") {
          this.registering = true;
          this.productService.registerSoftware(this.productKey).subscribe({
            next: (result: any) => {
              if (result.result && result.result == "error") {
                alert(result.message_plain);
                this.registering = false;
              } else {
                this.loadingProgressMessage = "Done! Refreshing your user data...";
                this.userService.getUserDetails(false).subscribe((userResult) => {
                  this.userService.getSubscriptionDetails(true).subscribe((subResult) => {
                    this.loadingProgressMessage = "Almost done, refreshing your product data...";
                    this.productService.getProducts(true).subscribe((updatedProducts) => {
                      this.productService.getLicenseDetails(result.assetId || result.licenseId).subscribe((licenseResult) => {
                        this.licenseResult = licenseResult;
                        this.registeredProduct = this.productService.productMap[this.licenseResult.product?.id];
                        let splashId = undefined;
                        if (this.registeredProduct) splashId = this.registeredProduct.stringId;
                        this.registerResponse = result;

                        this.fireRegistrationCompleted({
                          category: "software",
                          productName: this.registeredProduct?.title ?? this.licenseResult?.product?.name ?? "Product",
                          productType: "software",
                          productId: this.licenseResult?.product?.id ?? result?.assetId ?? result?.licenseId,
                        });
                        this.productRegistered.next(result);
                        this.registering = false;
                        let code = result.processed_case || null;
                        if (result.productCategory == "subscription") splashId = "fspplus";
                        this.checkForSplash(splashId, null, code, "software").then((hasSplash) => {
                          if (!hasSplash) {
                            this.checkReward();
                            this.dismissMessage = "Software successfully registered.";
                            if (this.registeredProduct?.id) {
                              this.showGiveawayGifts(this.registeredProduct.id, this.registeredProduct.title);
                            }
                          }
                          if (this.autoNavigate && result.productCategory != "subscription" && !this.optionCardOptions) this.viewRegisteredProduct(this.licenseResult.product.id);
                        });
                      });
                    });
                  });
                });
              }
            },
            error: (error) => {
              this.registering = false;
              this.fireRegistrationStarted({
                action: "Software Key",
                errorName: "serial not found",
              });
              this.appService.alertError(error);
            },
          });
        } else {
          if (this.date || this.mustang) {
            this.registering = true;
            let method: any;
            if (this.mustang) {
              method = this.productService.registerFenderPromo(rawKey);
            } else {
              method = this.productService.registerHardware(rawKey, format(this.date, "MM/dd/yyyy"), this.selectedOptionCardAssetId, !this.selectedOptionCardAssetId);
            }

            method.subscribe({
              next: (result: any) => {
                if (result?.isFenderGear) {
                  this.foundFenderGear = result?.snapshots;
                } else {
                  this.productService.getProducts(true).subscribe((updatedProducts) => {
                    this.userService.getSubscriptionDetails(true).subscribe((subResult) => {
                      if (result.productCategory == "subscription") {
                        let splashStringId = "fspplus";
                        this.checkForSplash(splashStringId, null, result.processed_case, "software").then((hasSplash) => {
                          if (!hasSplash) {
                            this.checkReward();
                            this.dismissMessage = "Software successfully registered.";
                          }
                        });
                      } else {
                        this.productService.getLicenseDetails(result.assetId || result.licenseId).subscribe((licenseResult) => {
                          this.licenseResult = licenseResult;
                          this.registeredProduct = this.productService.productMap[this.licenseResult.product.id];
                          this.registerResponse = result;
                          let code = result.giveAwayIds && result.giveAwayIds.length ? result.giveAwayIds[0].processed_case : null;
                          if (this.optionCardOptions) {
                            this.registerResponse = result;
                            this.fireRegistrationCompleted({
                              category: this.selectedType === "guitar" ? "instrument" : "recording hardware",
                              productName: this.registeredProduct?.title ?? "",
                              productType: this.selectedType === "guitar" ? "instrument" : "recording hardware",
                              productId: this.registeredProduct?.id,
                            });
                            this.productRegistered.next(result);
                            this.registering = false;
                            this.dismissMessage = "Hardware successfully registered.";
                          } else {
                            this.checkForSplash(this.registeredProduct.stringId, this.licenseResult.license.offers, code).then((hasSplash) => {
                              if (!hasSplash) {
                                this.registerResponse = result;
                                this.checkReward();
                                this.fireRegistrationCompleted({
                                  category: this.selectedType === "guitar" ? "instrument" : "recording hardware",
                                  productName: this.registeredProduct?.title ?? "",
                                  productType: this.selectedType === "guitar" ? "instrument" : "recording hardware",
                                  productId: this.registeredProduct?.id,
                                });
                                this.productRegistered.next(result);
                                this.registering = false;
                                this.dismissMessage = "Hardware successfully registered.";
                                if (this.licenseResult.license.offers && this.licenseResult.license.offers.length) {
                                  this.activeModal.close();
                                  const modalRef = this.appService.showModal(RedeemHardwareOfferComponent, { size: "lg" });
                                  modalRef.componentInstance.parentProduct = this.registeredProduct;
                                  modalRef.componentInstance.offers = this.licenseResult.license.offers;
                                  modalRef.componentInstance.license = this.licenseResult.license;
                                  modalRef.componentInstance.redeemed.subscribe((result) => {});
                                }
                                this.showGiveawayGifts(this.registeredProduct.id, this.registeredProduct.title);
                              }
                              if (this.autoNavigate) this.viewRegisteredProduct(this.registeredProduct.id);
                            });
                          }
                        });
                      }
                    });
                  });
                }
              },
              error: (error) => {
                this.registering = false;
                const errMsg = this.helperService.retrieveErrorMessage(error);
                this.fireRegistrationStarted({
                  action: "Serial Number",
                  errorName: "serial not found",
                });
                if (this.selectedType == "guitar") {
                  let modalRef = this.appService.showModal(MissingFenderSerialComponent, { size: "lg" });
                  modalRef.componentInstance.serial = this.productKey;
                  modalRef.componentInstance.inelligibleProductEntered.subscribe((result) => {
                    if (result == "software") {
                      this.appService.alert(this._translate.instant("products.register.inelligible_software"));
                      this.selectedType = "software";
                    } else if (result == "hardware") {
                      this.appService.alert(this._translate.instant("products.register.inelligible_hardware"));
                      this.activeModal.close();
                    }
                  });
                } else if (this.helperService.retrieveErrorMessage(error) == "Serial not found." && this.mustang) {
                  this.errorMsg =
                    "We are unable to validate the serial number you entered. Please try again or contact <a href='https://support.fender.com/en-US/contact-general/' target='_blank'>Fender Support</a>.";
                } else {
                  this.errorMsg = errMsg;
                }
              },
            });
          } else {
            this.fireRegistrationStarted({
              action: "Purchase Date",
              errorName: "Required field.",
            });
            this._translate.get("products.register.enter_required").subscribe((lang) => alert(lang));
          }
        }
      } else {
        this.fireRegistrationStarted({
          action: "Serial Number",
          errorName: "Invalid key format or length.",
        });
        this._translate.get("products.register.make_sure_key").subscribe((lang) => alert(lang));
      }
    } else {
      this.fireRegistrationStarted({
        action: "Serial Number",
        errorName: "Required field.",
      });
      this._translate.get("products.register.enter_required").subscribe((lang) => alert(lang));
    }
  }

  public foundFenderGear: any;
  performFenderRegister() {
    this.registering = true;
    this.productService.registerFenderProduct(this.productKey, undefined, false).subscribe({
      next: (result: any) => {
        this.registering = false;
        this.foundFenderGear = result;
        if (!Array.isArray(this.foundFenderGear)) {
          this.foundFenderGear = [this.foundFenderGear];
        }
      },
      error: (error) => {
        this.registering = false;
        const message = this.helperService.retrieveErrorMessage(error);
        if (this.helperService.retrieveErrorMessage(error) == "Serial not found." && this.mustang) {
          this.fireRegistrationStarted({ action: "Serial Number", errorName: "serial not found" });
          this.errorMsg =
            "We are unable to validate the serial number you entered. Please try again or contact <a href='https://support.fender.com/en-US/contact-general/' target='_blank'>Fender Support</a>.";
        } else {
          if (message.includes("No Fender gear found") || message.includes("No PIM product data found")) {
            this.fireRegistrationStarted({ action: "SKU / Model Number", errorName: "sku not found" });
            this.activeModal.close();
            let modalRef = this.appService.showModal(MissingFenderSerialComponent, { size: "lg" });
            modalRef.componentInstance.serial = this.productKey;
          } else {
            this.fireRegistrationStarted({
              action: "Serial Number",
              errorName: "serial not found",
            });
            this.errorMsg = message;
          }
        }
      },
    });
  }

  finalizeFenderRegister(gear: any) {
    this.registering = true;
    this.foundFenderGear = undefined;
    this.productService.registerFenderProduct(this.productKey, gear.product.fmic_material_number, true, format(this.date, "yyyy-MM-dd")).subscribe({
      next: (result: any) => {
        this.fireRegistrationCompleted({
          category: "instrument",
          productName: gear.product?.name ?? result?.productId ?? "Fender Product",
          productType: "instrument",
          productId: result?.productId ?? undefined,
          sku: gear.product?.fmic_material_number ?? undefined,
        });
        this.loadingProgressMessage = "Almost done, refreshing your product data...";
        this.productService.getProducts(true).subscribe((updatedProducts) => {
          this.registering = false;
          this.viewRegisteredProduct(parseInt(result.productId));
        });
      },
      error: (error) => {
        this.close();
        this.appService.alertError(error);
      },
    });
  }

  checkReward() {
    if (this.registerResponse?.reward) {
      this.rewards = [];
      for (var i in this.registerResponse.reward) {
        this.rewards.push(this.registerResponse.reward[i]);
      }
    }
  }

  checkForSplash(stringid, offers?, code?, registrationType?) {
    return new Promise((resolve, reject) => {
      this.fbService.checkForSplash(stringid).then((splash) => {
        if (splash) {
          this.showSplash(splash, offers, code, registrationType);
          resolve(true);
        } else if (code && code !== "granted" && code !== "pending") {
          let splashStringId = "fspplus";
          this.fbService.checkForSplash(null, splashStringId).then((splash) => {
            this.showSplash(splash, offers, code, registrationType);
            resolve(true);
          });
        } else {
          resolve(false);
        }
      });
    });
  }

  showSplash(splash, offers?, code?, registrationType = "hardware") {
    this.activeModal.close();
    if (code && this.userService.subDetails?.subscription?.bundle_time) {
      let translateKeyString = "hardware_bundle.processed_codes." + registrationType + "." + code;
      let bundleTextToInject = this._translate.instant(translateKeyString);
      let months = this.userService.subDetails.subscription.bundle_time;
      bundleTextToInject = bundleTextToInject.replace(/{{ months }}/g, months);
      let endDate = format(new Date(this.userService.subDetails.subscription.end_date), "MMMM do, yyyy");
      bundleTextToInject = bundleTextToInject.replace(/{{ endDate }}/g, endDate);
      if (this.userService.subDetails?.stripe_subscription?.discount?.coupon?.percent_off) {
        bundleTextToInject = bundleTextToInject.replace(/{{ percentDiscount }}/g, this.userService.subDetails.stripe_subscription.discount.coupon.percent_off);
      }
      const parser = new DOMParser();
      const doc = parser.parseFromString(splash.description, "text/html");
      const bundleTimeDescriptorDiv = doc.getElementById("bundle_time_descriptor");
      if (bundleTimeDescriptorDiv) {
        bundleTimeDescriptorDiv.innerHTML = bundleTextToInject;
        splash.description = doc.body.innerHTML;
      }
    }
    const modalRef = this.appService.showModal(RegisterSuccessSplashComponent, {
      size: "lg",
      container: this.appService.appContainer,
    });
    modalRef.componentInstance.splash = splash;
    modalRef.componentInstance.productId = this.registeredProduct?.id;
    modalRef.componentInstance.productName = this.registeredProduct?.title;
    modalRef.componentInstance.isNewRegistration = true;

    modalRef.componentInstance.closed.pipe(take(1)).subscribe((result) => {
      if (offers && offers.length) {
        const modalRef = this.appService.showModal(RedeemHardwareOfferComponent, { size: "lg" });
        modalRef.componentInstance.parentProduct = this.registeredProduct;
        modalRef.componentInstance.license = this.licenseResult.license;
        modalRef.componentInstance.offers = offers;
        modalRef.componentInstance.redeemed.subscribe((result) => {});
      }
      if (this.registeredProduct?.id) {
        this.showGiveawayGifts(this.registeredProduct.id, this.registeredProduct.title);
      }
    });
  }

  showGiveawayGifts(productId: number, productName: string) {
    if (!shouldShowGiveawayGiftsSplash(this.registerResponse)) {
      return;
    }
    this.productService.get(`giveaways/${productId}`).subscribe((gifts: any[]) => {
      if (gifts && gifts.length) {
        const modalRef = this.appService.showModal(GiveawayGiftsSplashComponent, {
          size: "lg",
          container: this.appService.appContainer,
        });
        modalRef.componentInstance.gifts = gifts;
        modalRef.componentInstance.registeredProductName = productName;
      }
    });
  }

  viewRegisteredProduct(productId) {
    this._router.navigate(["products/detail/", productId]);
    this.activeModal.close();
  }

  onSuccessClose() {
    this.fireRegistrationClosedCta();
    this.activeModal.close();
  }

  onHeaderClose() {
    this.fireRegistrationClosedCta();
    this.activeModal.dismiss(this.dismissMessage);
  }

  close(event?) {
    if (event) event.stopPropagation();
    this.fireRegistrationClosedCta();
    this.activeModal.close();
  }
}
